import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserModel } from '../data';
import { ApiService, isAuthInvalidError } from '../gateway';
import { EVENTS_TYPE, EventsService, ToastService } from '../services';
import { AUTH_CONSTANTS } from './auth.consts';
import { isMfaRequiredError, MfaChannel, MfaRequiredError, mfaTokenHeaders } from './mfa';
import { TokenService } from './token.service';

export class JwtTokens {
  accessToken: string;
  refreshToken: string;
}

export interface ISignin {
  email: string;
  password: string;
}

export interface ISignup {
  email: string;
  password: string;
}

export interface IForgot {
  email: string;
}

export interface IReset {
  id: string;
  token: string;
  password: string;
}

export interface IPassword {
  password: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenService = inject(TokenService);
  private readonly apiService = inject(ApiService);
  private readonly eventsService = inject(EventsService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionWatchTimer: ReturnType<typeof setInterval> | null = null;

  private readonly channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('erpclass_help_auth') : null;

  constructor() {
    this.channel?.addEventListener('message', ({ data }: MessageEvent) => {
      if (data?.type === 'TOKEN_REFRESHED') {
        this.tokenService.setToken(data.accessToken);
        if (data.refreshToken) this.tokenService.setRefreshToken(data.refreshToken);
        this.scheduleRefresh();
      } else if (data?.type === 'LOGOUT') {
        this.clearRefreshTimer();
        this.tokenService.clearToken();
      }
    });
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.resumeSession();
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.resumeSession());
    }
  }

  /**
   * Inicia o agendamento de refresh proativo quando há tokens persistidos.
   * Deve ser chamado uma vez no bootstrap da aplicação (AppComponent).
   */
  public initializeScheduler(): void {
    if (this.tokenService.token || this.tokenService.refreshToken) {
      this.scheduleRefresh();
    }
  }

  public async signup(params: ISignup): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_SIGNUP;
      const data: ISignin = { email: params.email, password: params.password };
      const tokens = await this.apiService.post<JwtTokens>({ path, data, base: 'auth' });
      this.saveTokens(tokens);
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  public async signin(params: ISignin): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_SIGNIN;
      const data: ISignin = { email: params.email, password: params.password };
      const tokens = await this.apiService.post<JwtTokens>({ path, data, base: 'auth' });
      this.saveTokens(tokens);
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  public async socialLoginGoogle(idToken: string): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_GOOGLE_LOGIN;
      const data = { idToken };
      const tokens = await this.apiService.post<JwtTokens>({ path, data, base: 'auth' });
      this.saveTokens(tokens);
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido ao autenticar com Google', { cause: e });
    }
  }

  public async socialLoginFacebook(accessToken: string): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_FACEBOOK_LOGIN;
      const data = { accessToken };
      const tokens = await this.apiService.post<JwtTokens>({ path, data, base: 'auth' });
      this.saveTokens(tokens);
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido ao autenticar com Facebook', { cause: e });
    }
  }

  /**
   * Ends THIS device's session on the server (`POST /logout` revokes only the token's sessionId —
   * other devices stay logged in) and always clears the local session, even when the server call
   * fails: a failed request must never leave someone logged in on a shared checkout computer.
   */
  public async logout(): Promise<void> {
    this.clearRefreshTimer();
    try {
      if (this.tokenService.token || this.tokenService.refreshToken) {
        await this.apiService.post({ path: AUTH_CONSTANTS.PATH_LOGOUT, base: 'auth' });
      }
    } catch {
      // Best effort: the local session is cleared below regardless; the server-side session
      // expires with its refresh token.
    } finally {
      this.channel?.postMessage({ type: 'LOGOUT' });
      this.tokenService.clearToken();
      this.eventsService.broadcast<unknown>(EVENTS_TYPE.IS_AUTH, undefined);
    }
  }

  public async forgot(params: IForgot): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_FORGOT;
      const data: IForgot = { email: params.email };
      await this.apiService.post({ path, data, base: 'auth' });
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  public async reset(params: IReset): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_RESET;
      const query: Record<string, string | number | boolean> = {
        id: params.id,
        token: params.token,
      };
      const data: IPassword & { token: string } = {
        password: params.password,
        token: params.token,
      };
      await this.apiService.post({ path, data, query, base: 'auth' });
    } catch (e: unknown) {
      const errorMessage = this.extractApiErrorMessage(e, 'Não foi possível redefinir a senha. Verifique se o link ainda é válido.');
      throw new Error(errorMessage, { cause: e });
    }
  }

  public async verify(): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_VERIFY;
      await this.apiService.post({ path, base: 'auth' });
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  public async confirm(verificationToken: string): Promise<void> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_CONFIRM;
      const query: Record<string, string | number | boolean> = { token: verificationToken };
      await this.apiService.post({ path, query, base: 'auth' });
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  private refreshInFlight: Promise<void> | null = null;

  public async refresh(): Promise<void> {
    if (this.refreshInFlight !== null) {
      return this.refreshInFlight;
    }
    const refreshTokenBefore = this.tokenService.refreshToken;
    if (!refreshTokenBefore) {
      throw new Error('Refresh token não encontrado');
    }
    this.refreshInFlight = (async () => {
      try {
        await this.withCrossTabRefreshLock(async () => {
          // Tabs share the refresh token (localStorage) and, after a broadcast, the
          // same access token — so their timers fire together. Rotation is single-use:
          // the tab that loses the race gets a 401 and would log every tab out.
          // Inside the lock, a rotated token means another tab already refreshed.
          const refreshToken = this.tokenService.refreshToken;
          if (!refreshToken) {
            throw new Error('Refresh token não encontrado');
          }
          if (refreshToken !== refreshTokenBefore && this.hasFreshAccessToken()) {
            return;
          }
          const path: string = AUTH_CONSTANTS.PATH_REFRESH;
          const data = { refreshToken };
          const tokens = await this.apiService.post<JwtTokens>({ path, data, base: 'auth' });
          this.saveTokens(tokens);
        });
      } catch (e: unknown) {
        if (e instanceof Error) {
          throw new Error(e.message, { cause: e });
        }
        throw new Error('Ocorreu um erro desconhecido', { cause: e });
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  /** Serializes refresh across tabs of this origin; runs directly where Web Locks is unavailable. */
  private async withCrossTabRefreshLock(task: () => Promise<void>): Promise<void> {
    const locks: LockManager | undefined = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks) {
      await task();
      return;
    }
    await locks.request(AuthService.REFRESH_LOCK_NAME, task);
  }

  /** True when this tab already holds an access token valid beyond the proactive-refresh margin. */
  private hasFreshAccessToken(): boolean {
    const expirationDate = this.tokenService.getExpirationDate();
    return expirationDate !== null && expirationDate.getTime() - Date.now() > 60_000;
  }

  /**
   * Asks auth if this access token still maps to a live session.
   * After an e-mail revoke the JWT remains signed, but TokenGuard rejects it.
   */
  public async validateSession(): Promise<void> {
    await this.apiService.get({ path: AUTH_CONSTANTS.PATH_SESSION, base: 'auth' });
  }

  public async profile(): Promise<UserModel> {
    try {
      const path: string = AUTH_CONSTANTS.PATH_PROFILE;
      const profile: UserModel = await this.apiService.get<UserModel>({ path, base: 'auth' });
      profile.organization = profile.organization ?? null;
      profile.identities = profile.identities ?? [];
      profile.hasPassword = profile.hasPassword !== false;
      profile.mfaEnabled = profile.mfaEnabled === true;
      return profile;
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e });
      }
      throw new Error('Ocorreu um erro desconhecido', { cause: e });
    }
  }

  public async unlinkIdentity(provider: 'google' | 'facebook' | 'whatsapp', mfaToken?: string): Promise<void> {
    try {
      await this.apiService.delete({
        path: `${AUTH_CONSTANTS.PATH_UNLINK_IDENTITY}/${provider}`,
        base: 'auth',
        headers: mfaToken ? mfaTokenHeaders(mfaToken) : undefined,
      });
    } catch (e: unknown) {
      this.throwAuthError(e, 'Não foi possível desvincular este método de acesso.');
    }
  }

  public async setPassword(password: string, mfaToken?: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_SET_PASSWORD,
        data: { password },
        base: 'auth',
        headers: mfaToken ? mfaTokenHeaders(mfaToken) : undefined,
      });
    } catch (e: unknown) {
      this.throwAuthError(e, 'Não foi possível definir a senha.');
    }
  }

  public async requestMfa(channel: MfaChannel): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_MFA_REQUEST,
        data: { channel },
        base: 'auth',
      });
    } catch (e: unknown) {
      this.throwAuthError(e, 'Não foi possível enviar o código de verificação.');
    }
  }

  public async verifyMfa(channel: MfaChannel, code: string): Promise<string> {
    try {
      const response = await this.apiService.post<{ mfaToken: string }>({
        path: AUTH_CONSTANTS.PATH_MFA_VERIFY,
        data: { channel, code },
        base: 'auth',
      });
      if (!response.mfaToken) {
        throw new Error('Não foi possível confirmar o segundo fator.');
      }
      return response.mfaToken;
    } catch (e: unknown) {
      this.throwAuthError(e, 'Código inválido ou expirado. Solicite um novo.');
    }
  }

  public async enableMfa(mfaToken: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_MFA_ENABLE,
        base: 'auth',
        headers: mfaTokenHeaders(mfaToken),
      });
    } catch (e: unknown) {
      this.throwAuthError(e, 'Não foi possível ativar a verificação em duas etapas.');
    }
  }

  public async disableMfa(mfaToken: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_MFA_DISABLE,
        base: 'auth',
        headers: mfaTokenHeaders(mfaToken),
      });
    } catch (e: unknown) {
      this.throwAuthError(e, 'Não foi possível desativar a verificação em duas etapas.');
    }
  }

  /**
   * Persists tokens after passwordless login without duplicating session bookkeeping.
   */
  public completeSession(tokens: JwtTokens): void {
    this.saveTokens(tokens);
  }

  private throwAuthError(error: unknown, fallbackMessage: string): never {
    if (isMfaRequiredError(error)) {
      throw error instanceof MfaRequiredError ? error : new MfaRequiredError();
    }
    if (error instanceof Error) {
      throw new Error(error.message, { cause: error });
    }
    throw new Error(fallbackMessage, { cause: error });
  }

  private saveTokens(tokens: JwtTokens): void {
    this.tokenService.setToken(tokens.accessToken);
    if (tokens.refreshToken) {
      this.tokenService.setRefreshToken(tokens.refreshToken);
    }
    this.channel?.postMessage({
      type: 'TOKEN_REFRESHED',
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });
    this.scheduleRefresh();
  }

  private scheduleRefresh(): void {
    this.startSessionWatch();
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    const expirationDate = this.tokenService.getExpirationDate();
    if (!expirationDate) {
      return;
    }
    const delay = Math.max(0, expirationDate.getTime() - Date.now() - 60_000);
    this.refreshTimer = setTimeout(() => this.runScheduledRefresh(), delay);
  }

  private static readonly TRANSIENT_RETRY_INITIAL_DELAY_MS = 5_000;
  private static readonly TRANSIENT_RETRY_MAX_DELAY_MS = 30_000;
  private static readonly REFRESH_LOCK_NAME = 'erpclass_help_auth_refresh';
  private transientRetryDelayMs = AuthService.TRANSIENT_RETRY_INITIAL_DELAY_MS;

  /**
   * Runs the proactive refresh and, on a transient failure (network drop, auth
   * restarting, 5xx, 409 race, 429), keeps retrying with a capped backoff instead
   * of logging out — only a real 401/403 means the session is invalid.
   */
  private runScheduledRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.refresh()
      .then(() => (this.transientRetryDelayMs = AuthService.TRANSIENT_RETRY_INITIAL_DELAY_MS))
      .catch((error: unknown) => {
        if (isAuthInvalidError(error)) {
          this.handleProactiveRefreshFailure();
          return;
        }
        this.refreshTimer = setTimeout(() => this.runScheduledRefresh(), this.transientRetryDelayMs);
        this.transientRetryDelayMs = Math.min(this.transientRetryDelayMs * 2, AuthService.TRANSIENT_RETRY_MAX_DELAY_MS);
      });
  }

  /**
   * A POS that slept, or a tab the browser throttled in the background, may come back with
   * an expired access token. Refresh right away on focus/reconnect so the operator's next
   * action does not have to wait on a 401 round-trip first.
   */
  private resumeSession(): void {
    if (!this.tokenService.refreshToken) return;
    if (!this.hasFreshAccessToken()) {
      this.runScheduledRefresh();
      return;
    }
    this.checkSession();
  }

  /**
   * Trata falha real de autenticação (401/403) do refresh/validate proativo.
   * Limpa sessão, sincroniza abas e redireciona — mesmo fluxo do interceptor em 401.
   */
  private handleProactiveRefreshFailure(): void {
    this.clearRefreshTimer();
    this.tokenService.clearToken();
    this.channel?.postMessage({ type: 'LOGOUT' });
    this.eventsService.broadcast<unknown>(EVENTS_TYPE.IS_AUTH, undefined);
    this.toastService.warning({ message: 'Sua sessão expirou. Faça login novamente.' });
    void this.router.navigate(['/signin']);
  }

  private startSessionWatch(): void {
    if (this.sessionWatchTimer) return;
    this.sessionWatchTimer = setInterval(() => {
      // Hidden tabs skip the poll (resumeSession checks on return) — keeps a store
      // full of background tabs from flooding auth through one shared IP.
      if (typeof document !== 'undefined' && document.hidden) return;
      this.checkSession();
    }, 20_000);
  }

  /** Detects a revoked session; ignores transient failures, which say nothing about it. */
  private checkSession(): void {
    if (!this.tokenService.token && !this.tokenService.refreshToken) return;
    void this.validateSession().catch((error: unknown) => {
      if (isAuthInvalidError(error)) {
        this.handleProactiveRefreshFailure();
      }
    });
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.sessionWatchTimer) {
      clearInterval(this.sessionWatchTimer);
      this.sessionWatchTimer = null;
    }
  }

  private extractApiErrorMessage(error: unknown, fallbackMessage: string): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (error && typeof error === 'object') {
      const errorRecord = error as Record<string, unknown>;
      const message = errorRecord['message'];
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
      if (Array.isArray(message)) {
        const messages: string[] = message.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
        if (messages.length > 0) {
          return messages.join(' | ');
        }
      }
    }
    return fallbackMessage;
  }
}
